import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Image, Upload, Trash2, Loader2, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortfolioGallery = ({ userId, editable = false }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ caption: '', project_name: '' });

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portfolio/${userId}`);
      setImages(res.data || []);
    } catch (err) {
      console.error('Portfolio fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchPortfolio();
  }, [userId, fetchPortfolio]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop grande (max 10MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/portfolio/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo ajoutée au portfolio');
      fetchPortfolio();
    } catch (err) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    try {
      await axios.delete(`${API}/portfolio/${imageId}`, { withCredentials: true });
      toast.success('Photo supprimée');
      setImages(prev => prev.filter(i => i.id !== imageId));
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const handleEdit = async (imageId) => {
    try {
      await axios.put(`${API}/portfolio/${imageId}`, editForm, { withCredentials: true });
      toast.success('Mis à jour');
      setEditingId(null);
      fetchPortfolio();
    } catch (err) {
      toast.error('Erreur de mise à jour');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#1E3A8A]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="portfolio-gallery">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Image className="w-5 h-5 text-[#1E3A8A]" />
          Portfolio
          {images.length > 0 && <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A]">{images.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editable && (
          <div className="mb-4">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleUpload}
                className="hidden"
                data-testid="portfolio-upload-input"
              />
              <div className="border-2 border-dashed border-[#1E3A8A]/20 rounded-lg p-4 text-center hover:border-[#1E3A8A]/40 transition-colors">
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1E3A8A]" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 mx-auto mb-2 text-[#6B7280]" />
                    <p className="text-sm text-[#6B7280]">Cliquez pour ajouter une photo de chantier</p>
                  </>
                )}
              </div>
            </label>
          </div>
        )}

        {images.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((img) => (
              <div key={img.id} className="relative group" data-testid={`portfolio-item-${img.id}`}>
                <img
                  loading="lazy"
                  src={img.image_url}
                  alt={img.caption || 'Portfolio'}
                  className="w-full h-40 object-cover rounded-lg"
                />
                {(img.caption || img.project_name) && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 rounded-b-lg">
                    {img.project_name && <p className="text-xs font-medium text-white">{img.project_name}</p>}
                    {img.caption && <p className="text-xs text-white/80">{img.caption}</p>}
                  </div>
                )}
                {editable && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 w-7 p-0 bg-white/90"
                      onClick={() => {
                        setEditingId(img.id);
                        setEditForm({ caption: img.caption || '', project_name: img.project_name || '' });
                      }}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 w-7 p-0 bg-white/90 text-red-500"
                      onClick={() => handleDelete(img.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {editingId === img.id && (
                  <div className="absolute inset-0 bg-black/80 rounded-lg p-3 flex flex-col justify-center gap-2">
                    <Input
                      placeholder="Nom du projet"
                      value={editForm.project_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, project_name: e.target.value }))}
                      className="bg-white text-sm h-8"
                    />
                    <Input
                      placeholder="Description"
                      value={editForm.caption}
                      onChange={(e) => setEditForm(prev => ({ ...prev, caption: e.target.value }))}
                      className="bg-white text-sm h-8"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => handleEdit(img.id)}>OK</Button>
                      <Button size="sm" variant="ghost" className="flex-1 h-7 text-xs text-white" onClick={() => setEditingId(null)}>Annuler</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-[#6B7280]">
            <Image className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">{editable ? 'Ajoutez vos photos de chantiers terminés' : 'Aucune photo dans le portfolio'}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioGallery;
